import { ORIGIN_GHOST_OPACITY } from "../utils/layerTree";

export const layerTryPreviewKey = ({ artboardID, nodeID } = {}) =>
  `${String(artboardID || "").trim()}:${String(nodeID || "").trim()}`;

export const revokeLayerTryPreview = (preview) => {
  if (preview?.type === "image" && preview.blobUrl) {
    URL.revokeObjectURL(preview.blobUrl);
  }
};

export const revokeLayerTryPreviews = (previews = {}) => {
  Object.values(previews).forEach(revokeLayerTryPreview);
};

export const parseLayerTryPreviewKey = (key) => {
  const value = String(key || "");
  const separator = value.indexOf(":");
  if (separator < 0) {
    return { artboardID: "", nodeID: value };
  }
  return {
    artboardID: value.slice(0, separator),
    nodeID: value.slice(separator + 1),
  };
};

export const applySessionLayerTryPreview = ({ box, preview } = {}) => {
  if (!box || !preview) return box;
  switch (preview.type) {
    case "image":
      return preview.blobUrl
        ? { ...box, assetUrl: preview.blobUrl, assetOpacity: ORIGIN_GHOST_OPACITY }
        : box;
    case "text":
      return preview.text
        ? { ...box, text: preview.text, textOpacity: ORIGIN_GHOST_OPACITY }
        : box;
    default: {
      const exhaustive = preview.type;
      throw new Error(`Unhandled layer try preview type: ${exhaustive}`);
    }
  }
};

const walkNodes = ({ nodes = {}, onNode }) => {
  Object.values(nodes || {}).forEach((node) => {
    if (!node) return;
    onNode(node);
    walkNodes({ nodes: node.children || {}, onNode });
  });
};

const listNodeAssetRefs = (node) => {
  const refs = [];
  const origin = String(node?.originAssetRef || "").trim();
  if (origin) refs.push(origin);
  const extra = node?.source?.referenceAssets;
  const list = Array.isArray(extra)
    ? extra
    : extra && typeof extra === "object"
      ? Object.values(extra)
      : [];
  list.forEach((ref) => {
    const key = String(ref || "").trim();
    if (key) refs.push(key);
  });
  return refs;
};

export const collectPreviewLayerAssets = ({ node, assets = {} } = {}) => {
  const next = {};
  listNodeAssetRefs(node).forEach((key) => {
    const url = String(assets[key]?.url || "").trim();
    if (url) next[key] = { url };
  });
  return next;
};

export const collectPreviewArtboardAssets = ({ nodes = {}, assets = {} } = {}) => {
  const next = {};
  walkNodes({
    nodes,
    onNode: (node) => {
      listNodeAssetRefs(node).forEach((key) => {
        if (next[key]) return;
        const url = String(assets[key]?.url || "").trim();
        if (url) next[key] = { url };
      });
    },
  });
  return next;
};

export const collectPreviewSampleRow = ({ nodes = {}, assets = {} } = {}) => {
  const row = {};
  walkNodes({
    nodes,
    onNode: (node) => {
      if (node?.source?.kind !== "data") return;
      const key = String(node.source.variable || "").trim();
      if (!key || row[key]) return;
      if (node.type === "image") {
        const ref = String(node.originAssetRef || "").trim();
        const url = String(assets[ref]?.url || "").trim();
        if (url) row[key] = url;
        return;
      }
      const text = String(node.originText || "").trim();
      if (text) row[key] = text;
    },
  });
  return row;
};

export const pickPreviewLayerNode = (node) => {
  if (!node) return null;
  return {
    id: node.id,
    type: node.type,
    name: node.name,
    source: node.source,
    originAssetRef: node.originAssetRef,
    originText: node.originText,
    crop: node.crop,
    fit: node.fit,
    overflow: node.overflow,
    style: node.style,
    isBackground: node.isBackground,
  };
};

export const base64PngToBlobUrl = (base64) => {
  const raw = String(base64 || "").trim();
  if (!raw) return "";
  const binary = atob(raw);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
};
